import _ from "lodash";
import { connect } from "react-redux";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownMenu,
  FilterLabelButton,
  Modal,
} from "@toddle-edu/ds-web";

// * Internal
import { useI18n } from "Hooks";
import Loading from "UIComponents/Loading/Loading";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

// * Relative
import { useAssessmentLearningGoalsData } from "../../module/AssessmentLearningGoalsModule";
import DiscardConfirmationDialog from "./components/DiscardConfirmationDialog";
import AssessmentLearningGoalsSearchInput from "./components/AssessmentLearningGoalsSearchInput";
import AssessmentOtherLearningGoalsModalBody from "./components/AssessmentOtherLearningGoalsModalBody";
import {
  ASSESSMENT_LEARNING_GOALS_OPTIONS,
  getAssessmentLearningGoalsTreeData,
  getAssessmentLearningGoalsInitialValue,
  getAssessmentLearningGoalsQueryVariable,
  getLearningGoalData,
  getLearningGoalsHeaderDropdownOptions,
  MYP_OBJECTIVES_FIELD_KEYS,
} from "../../module/AssessmentLearningGoalsUtils";
import classes from "./AssessmentOtherLearningGoals.scss";

// * Styles (kept for external components only)
const STYLES = {
  modalHeader: {
    marginBottom: 0,
  },
  modalBody: {
    width: "100%",
    padding: "0px",
    display: "flex",
    overflow: "hidden auto",
  },
};

// * Assessment Other Learning Goals Component - Modal Header
const ActionElements = ({
  unitPlan,
  isLoading,
  selectedGoalsValues,
  setSelectedGoalsValues,
  learningGoal,
  learningGoalsData,
  showOnlyUnitSelections,
  setShowOnlyUnitSelections,
}) => {
  const { t } = useI18n();

  const showHeaderDropdown =
    unitPlan?.value && learningGoal?.isPlannerElement && !isLoading;

  const selectedDropdownLabel = showOnlyUnitSelections
    ? t("scopeAndSequence:tagged_in_label", {
        label: _.get(unitPlan, "resolvedMinimalTree.unitPlan.title.value", ""),
      })
    : t("common:all");

  const dropdownOptions = useMemo(
    () => getLearningGoalsHeaderDropdownOptions({ t, unitPlan }),
    [t, unitPlan]
  );

  const handleDropdownSelection = selectedOption => {
    if (selectedOption.key === "all") {
      setShowOnlyUnitSelections(false);
    } else {
      setShowOnlyUnitSelections(true);
    }
  };

  return (
    !isLoading && (
      <>
        {showHeaderDropdown && (
          <Dropdown
            overlay={
              <DropdownMenu
                dsVersion="2.0"
                options={dropdownOptions}
                value={selectedDropdownLabel}
                onClick={handleDropdownSelection}
              />
            }
          >
            <FilterLabelButton
              dsVersion="2.0"
              size="medium"
              label={t("common:show_with_label", {
                label: t("common:learning_goals"),
              })}
              labelValue={selectedDropdownLabel}
              type="outlined"
              testId="assessment-other-learning-goals-only-unit-selection"
              labelStyle={{
                width: "144px",
                textAlign: "left",
              }}
            />
          </Dropdown>
        )}

        <AssessmentLearningGoalsSearchInput
          selectedGoalsValues={selectedGoalsValues}
          setSelectedGoalsValues={setSelectedGoalsValues}
          learningGoalsData={learningGoalsData}
        />
      </>
    )
  );
};

// * Assessment Other Learning Goals Component
const AssessmentOtherLearningGoals = ({
  t,
  onClose,
  unitPlan,
  unitPlanFields,
  assessmentFields,
  allLearningGoals,
  updateInputField,
  hasAddRemoveValues,
  selectedLearningGoalKey,
  getLearningGoalsTagCountTooltip,
  unitPlanTemplate,
  subFields,
  curriculumType,
  curriculumId,
  curriculumNodeInterfaceType,
  plannerElementTypes,
  learningGoalsQueryVariables,
  academicYearId,
}) => {
  const { learningGoalsData, isLoading } = useAssessmentLearningGoalsData({
    curriculumId,
    curriculumNodeInterfaceType,
    plannerElementTypes,
    learningGoalsQueryVariables,
    academicYearId,
  });

  const initialValues = useMemo(
    () =>
      getAssessmentLearningGoalsInitialValue({
        assessmentFields,
        allLearningGoals,
      }),
    [assessmentFields, allLearningGoals]
  );

  const [selectedGoalsValues, setSelectedGoalsValues] = useState(initialValues);
  const [showOnlyUnitSelections, setShowOnlyUnitSelections] = useState(null);
  const [discardConfirmation, setDiscardConfirmation] = useState(false);
  const [selectedLearningGoal, setSelectedLearningGoal] = useState(
    selectedLearningGoalKey
  );

  const learningGoal = _.find(allLearningGoals, { key: selectedLearningGoal });

  const hasUnsavedChanges = !_.isEqual(selectedGoalsValues, initialValues);

  // Set default showOnlyUnitSelections as true if unit is tagged
  useEffect(() => {
    if (showOnlyUnitSelections !== null || isLoading || _.isEmpty(unitPlan))
      return;

    setShowOnlyUnitSelections(Boolean(unitPlan?.value && !isLoading));
  }, [isLoading, showOnlyUnitSelections, unitPlan, unitPlan?.value]);

  const allLearningGoalsData = useMemo(() => {
    return _.map(allLearningGoals, learningGoal => {
      const {
        isPlannerElement = false,
        key = "",
        type = "",
        subType,
      } = learningGoal;

      // Backend data doesn't have subType field; use isSubTypeDependent flag to differentiate
      const plannerElementData = !_.isEmpty(subType)
        ? _.find(
            learningGoalsData,
            item => item.type === type && item.isSubTypeDependent
          )
        : _.find(
            learningGoalsData,
            item => item.type === type && !item.isSubTypeDependent
          );

      const fieldData = getLearningGoalData({
        fieldKey: key,
        plannerElementType: type,
        unitPlanFields,
        unitPlanTemplate,
        subFields,
        curriculumType,
        assessmentFields,
        isPlannerElement,
        plannerElementNodes: _.get(plannerElementData, "nodes", []),
      });

      return {
        ...learningGoal,
        ...fieldData,
      };
    });
  }, [
    learningGoalsData,
    allLearningGoals,
    unitPlanFields,
    unitPlanTemplate,
    subFields,
    assessmentFields,
    curriculumType,
  ]);

  // * Memoized learning goal data with unit specific nodes or global nodes
  const learningGoalsTreeData = useMemo(() => {
    const goalsWithUnitNodes = _.map(allLearningGoalsData, lgData => {
      const unitSpecificNodes = _.get(lgData, "unitSpecificNodes", []);
      const isPlannerElement = _.get(lgData, "isPlannerElement", false);

      return {
        ...lgData,
        ...(showOnlyUnitSelections &&
          isPlannerElement && { nodes: unitSpecificNodes }),
      };
    });

    return getAssessmentLearningGoalsTreeData({
      allLearningGoals,
      learningGoalsData: goalsWithUnitNodes,
    });
  }, [allLearningGoals, showOnlyUnitSelections, allLearningGoalsData]);

  const totalSelectedCount = useMemo(
    () => _.sumBy(_.values(selectedGoalsValues), arr => _.size(arr)),
    [selectedGoalsValues]
  );

  // * Update learning goals handler
  const handleAddLearningGoals = useCallback(async () => {
    _.forEach(selectedGoalsValues, (thisValue, key) => {
      if (_.isEqual(thisValue, initialValues[key])) return;
      updateInputField(
        { [key]: thisValue },
        { nodes: _.find(allLearningGoalsData, { key })?.nodes },
        true,
        hasAddRemoveValues
      );
    });
    onClose();
  }, [
    selectedGoalsValues,
    initialValues,
    allLearningGoalsData,
    updateInputField,
    hasAddRemoveValues,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setDiscardConfirmation(true);
      return;
    }
    onClose();
  }, [hasUnsavedChanges, onClose]);

  const handleDiscardCancel = useCallback(
    () => setDiscardConfirmation(false),
    []
  );

  const handleDiscardConfirm = useCallback(() => {
    setDiscardConfirmation(false);
    onClose();
  }, [onClose]);

  return (
    <>
      <Modal
        width="90%"
        height="90%"
        dsVersion="2.0"
        onClose={handleClose}
        hasOverlay={true}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={false}
      >
        <Modal.Header
          title={t("journal:select_learning_goals")}
          style={STYLES.modalHeader}
          hasSeparator={true}
          actionElement={
            <ActionElements
              unitPlan={unitPlan}
              isLoading={isLoading}
              selectedGoalsValues={selectedGoalsValues}
              setSelectedGoalsValues={setSelectedGoalsValues}
              learningGoal={learningGoal}
              learningGoalsData={learningGoalsTreeData}
              showOnlyUnitSelections={showOnlyUnitSelections}
              setShowOnlyUnitSelections={setShowOnlyUnitSelections}
            />
          }
        />

        <Modal.Body style={STYLES.modalBody}>
          {isLoading ? (
            <Loading containerStyle={STYLES.modalBody} />
          ) : (
            <AssessmentOtherLearningGoalsModalBody
              selectedGoalsValues={selectedGoalsValues}
              setSelectedGoalsValues={setSelectedGoalsValues}
              allLearningGoals={allLearningGoals}
              learningGoalsData={learningGoalsTreeData}
              selectedLearningGoal={selectedLearningGoal}
              setSelectedLearningGoal={setSelectedLearningGoal}
              getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
            />
          )}
        </Modal.Body>
        <Modal.Footer hasSeparator={true}>
          <div className={classes.modalFooterContainer}>
            <div className="text-label">
              {t("journal:count_pyp_element_selected", {
                count: totalSelectedCount,
              })}
            </div>
            <div className={classes.modalFooterAction}>
              <Button
                dsVersion="2.0"
                type="outlined"
                variant="neutral"
                onClick={handleClose}
                testId="assessment-other-learning-goals-cancel-button"
              >
                {t("common:cancel")}
              </Button>
              <Button
                type="fill"
                dsVersion="2.0"
                variant="progressive"
                disabled={!hasUnsavedChanges}
                onClick={handleAddLearningGoals}
                testId="assessment-other-learning-goals-select-and-add-button"
              >
                {t("common:done")}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {discardConfirmation && (
        <DiscardConfirmationDialog
          onCancel={handleDiscardCancel}
          onConfirm={handleDiscardConfirm}
        />
      )}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    courseId,
    courseSubjects,
    assessmentFields,
    assignmentSubjects,
    field_list,
    defaultOptions,
    unitPlanTemplate,
    selectedLearningGoalKey,
  } = ownProps;

  const academicYearId = getSelectedAcademicYearId(state);
  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    {}
  );
  const {
    unitPlan,
    allLearningGoals,
    mypObjectivesOptions,
    learningGoalsQueryVariables,
  } = getAssessmentLearningGoalsQueryVariable({
    t,
    courseId,
    academicYearId,
    courseSubjects,
    assessmentFields,
    assignmentSubjects,
    field_list,
    defaultOptions,
    unitPlanTemplate,
    plannerElementSets,
  });

  // * When MYP_OBJECTIVES is selected, use mypObjectivesOptions; otherwise use allLearningGoals
  const isMypObjectivesSelected =
    selectedLearningGoalKey ===
      ASSESSMENT_LEARNING_GOALS_OPTIONS.MYP_OBJECTIVES ||
    _.includes(MYP_OBJECTIVES_FIELD_KEYS, selectedLearningGoalKey);

  const learningGoalsToShow = isMypObjectivesSelected
    ? mypObjectivesOptions
    : allLearningGoals;

  const learningGoalsToShowKeys = _.map(learningGoalsToShow, "key");

  const curriculumProgram = ownProps?.curriculumProgram;
  const curriculumId = _.get(curriculumProgram, "id");
  const curriculumNodeInterfaceType = _.get(
    curriculumProgram,
    "nodeInterfaceType"
  );
  const curriculumType = _.get(curriculumProgram, "type");
  const plannerElementTypes = _.compact(_.map(learningGoalsToShow, "type"));

  let updatedSelectedlearningGoalKey = selectedLearningGoalKey;
  if (!_.includes(learningGoalsToShowKeys, selectedLearningGoalKey)) {
    updatedSelectedlearningGoalKey = _.first(learningGoalsToShowKeys);
  }

  return {
    unitPlan,
    academicYearId,
    allLearningGoals: learningGoalsToShow,
    learningGoalsData: [],
    curriculumType,
    curriculumId,
    curriculumNodeInterfaceType,
    plannerElementTypes,
    learningGoalsQueryVariables,
    selectedLearningGoalKey: updatedSelectedlearningGoalKey,
  };
};

export default connect(mapStateToProps, null)(AssessmentOtherLearningGoals);
